import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BoxModelConstraint, BusinessComponent, BusinessComponentOptions, CellPanel, PageBreak, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { ComponentData, TabUtil } from '@ddt-web/data-service';
import * as get from 'lodash/get';

import { HeadingComponent, HeadingLevel } from '../heading/heading-component';
import { ReducedSpacingConfig } from '../helpers/config';
import { SectionTitleOptions } from './section-title-options';

export class SectionTitleComponent extends BusinessComponent {
	private reducedSpacingConfig: any = ReducedSpacingConfig.sectionTitle;
	constructor(
		public dcrKey: string,
		public defaultText: string,
		public options: BusinessComponentOptions,
		public showIfNoDataAvailable: boolean = false,
		public sectionTitleOptions: SectionTitleOptions = {},
		public shouldStartFromNewPage?: boolean,
		private shouldReduceSpacing: boolean = false,
		private headingLevel: HeadingLevel = HeadingLevel.H1
	) {
		super(defaultText, 'BusinessComponent', BusinessComponentOptions.merge(options));
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		return Boolean(componentData && componentData.success && componentData.tabs && componentData.tabs.length);
	}

	/**
	 * Builds the component for the panel.
	 * If there is no data available for that componentData, an empty array is returned.
	 */
	public buildComponent(componentData: ComponentData, contentSvc: ContentService): AbstractRenderer<SVGGElement>[] {
		const pageBreakRenderer = this.shouldStartFromNewPage ? [new PageBreak()] : [];
		const headingConstraints = this.shouldReduceSpacing ? this.reducedSpacingConfig.heading : { paddingTop: 0, paddingBottom: 8 };
		if (!this.options.componentId) {
			return [...pageBreakRenderer, new HeadingComponent(this.dcrKey, this.defaultText, this.headingLevel, headingConstraints)];
		} else if (this.isComponentDataVisible(componentData)) {
			if (!TabUtil.areRawValuesNotAvailable(componentData)) {
				let header: AbstractRenderer<SVGGElement>[] = pageBreakRenderer;
				if (this.defaultText === 'OVERVIEW') {
					header = [...header, new HeadingComponent(this.dcrKey, this.defaultText, this.headingLevel, { paddingTop: 0 })];
				} else {
					if (get(this.sectionTitleOptions, 'subTextDcr') || get(this.sectionTitleOptions, 'subTextDefaultText')) {
						header = [...header, ...this.buildHeaderWithSubText(headingConstraints)];
					} else {
						header = [...header, new HeadingComponent(this.dcrKey, this.defaultText, this.headingLevel, headingConstraints)];
					}
				}

				if (get(this.sectionTitleOptions, 'disclaimerDcr') || get(this.sectionTitleOptions, 'disclaimerDefaultText')) {
					header = this.addDisclaimerToHeader(header);
				}
				return header;
			}
			if (this.showIfNoDataAvailable) {
				return [new HeadingComponent(this.dcrKey, this.defaultText, this.headingLevel, headingConstraints)];
			}
		}
		return [];
	}

	public buildHeaderWithSubText(headingConstraints: Partial<BoxModelConstraint>): AbstractRenderer<SVGGElement>[] {
		return [
			new RowPanel(
				this.defaultText,
				[
					new CellPanel(
						[
							new HeadingComponent(this.dcrKey, this.defaultText, this.headingLevel, headingConstraints)
						],
						{ proportion: 80 },
						Alignment.Horizontal.Left
					),
					new CellPanel(
						[
							new StaticParagraphComponent(
								get(this.sectionTitleOptions, 'subTextDcr'),
								get(this.sectionTitleOptions, 'subTextDefaultText'),
								{},
								{ paddingTop: 15, paddingBottom: 8 },
								['text-bold']
							)
						],
						{ useFullHeight: true, proportion: 20 },
						Alignment.Horizontal.Right
					)

				],
				{},
				Alignment.Horizontal.Left,
				Alignment.Vertical.Bottom
			)];
	}

	private addDisclaimerToHeader(existingHeader: AbstractRenderer<SVGGElement>[]): AbstractRenderer<SVGGElement>[] {
		return [
			...existingHeader,
			new StaticParagraphComponent(
				get(this.sectionTitleOptions, 'disclaimerDcr'),
				get(this.sectionTitleOptions, 'disclaimerDefaultText'),
				{},
				{ paddingTop: 0, paddingBottom: 8 },
				get(this.sectionTitleOptions, 'subTextDcr')
			)
		];
	}
}
